// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Checkbox, FormControlLabel, Grid2, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import {
  BaseInput,
  BaseSelect,
  FileUpload,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { LaborService, MaterialService, TotalService } from '~/components/module/service';
// Others
import {
  convertDataUpdate,
  defaultValuesForm,
  defaultValuesFormLabor,
  defaultValuesFormMaterial,
  schema,
} from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  BACK_TO_PREVIOUS_PAGE,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE,
} from '~/utils/constants/common';
import {
  IFormService,
  IFormUpdateService,
  IServiceDetail,
  IServiceLabor,
  IServiceMaterial,
} from '~/utils/interface/service';
import { IBaseOption, IFileUpload } from '~/utils/interface/common';
import { mockDataServiceUnit } from '~/utils/mockData/service';
import { compareDataUpdate, createFormData, isEmptyObject, removeEmptyObjects } from '~/utils/helper/common';
import { createService, getServiceDetail, updateService } from '~/thunks/service/serviceThunk';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { ICategory, IQueryGetListCategory } from '~/utils/interface/category';
import { TypeMasterDataEnum } from '~/utils/enum/common';
import useDebounce from '~/utils/hooks/useDebounce';
// Styles, images, icons
import styles from './FormService.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const FormService = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/service', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: serviceId } = useParams();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IFormService>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const {
    fields: fieldsMaterials,
    append: appendMaterials,
    remove: removeMaterials,
  } = useFieldArray({ control, name: 'materials' });

  const {
    fields: fieldsLabor,
    append: appendLabor,
    remove: removeLabor,
  } = useFieldArray({ control, name: 'labors' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormService>(defaultValuesForm);
  const [listCategory, setListCategory] = useState<IBaseOption[]>([]);
  const [isLoadingCategory, setIsLoadingCategory] = useState<boolean>(false);
  const [categorySearchKey, setCategorySearchKey] = useState<string>(EMPTY_STRING);
  const debounceCategorySearchKey = useDebounce(categorySearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  //#endregion Declare State

  //#region Implement Hook

  const handleGetCategoryList = useCallback(
    (params: IQueryGetListCategory) => {
      setIsLoadingCategory(true);
      dispatch(getListCategory(params))
        .unwrap()
        .then((res) => {
          const { responses } = res?.data;
          if (!responses) return;
          const newData: IBaseOption[] = responses.map((item: ICategory) => ({
            value: item?.id,
            label: item?.name,
          }));
          setListCategory(newData);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingCategory(false);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!serviceId) return;

    handleGetServiceDetail(serviceId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    const payload: IQueryGetListCategory = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      type: TypeMasterDataEnum.CATEGORY_SERVICE,
      ...(debounceCategorySearchKey && { textSearch: debounceCategorySearchKey }),
    };

    handleGetCategoryList(payload);
  }, [debounceCategorySearchKey, handleGetCategoryList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetServiceDetail = (serviceId: string) => {
    if (!serviceId) return;

    setIsLoading(true);

    dispatch(getServiceDetail(serviceId))
      .unwrap()
      .then((res) => {
        const serviceDetail: IServiceDetail = res?.data;

        const newData: IFormService = {
          name: serviceDetail?.name || EMPTY_STRING,
          unit: serviceDetail?.unit || EMPTY_STRING,
          description: serviceDetail?.description || EMPTY_STRING,
          taskCode: serviceDetail?.taskCode || EMPTY_STRING,
          isTaxable: serviceDetail?.isTaxable || false,
          serviceImage: serviceDetail?.serviceImage
            ? {
                url: serviceDetail?.serviceImage,
              }
            : {},
          categoryId: serviceDetail?.category?.id || EMPTY_STRING,
          totalCost: serviceDetail?.cost || DEFAULT_NUMBER_ZERO,
          totalPrice: serviceDetail?.price || DEFAULT_NUMBER_ZERO,
          materials:
            serviceDetail?.materials?.map((material: IServiceMaterial) => {
              return {
                id: material?.id || EMPTY_STRING,
                materialId: material?.materialId || EMPTY_STRING,
                description: material?.description || EMPTY_STRING,
                unitPrice: material?.unitPrice?.toString() || EMPTY_STRING,
                quantity: material?.quantity?.toString() || EMPTY_STRING,
                unitCost: material?.unitCost?.toString() || EMPTY_STRING,
                totalCost: material?.totalCost?.toString() || EMPTY_STRING,
                totalPrice: material?.totalPrice?.toString() || EMPTY_STRING,
              };
            }) || [],
          labors:
            serviceDetail?.labors.map((labor: IServiceLabor) => {
              return {
                id: labor?.id || EMPTY_STRING,
                description: labor?.description || EMPTY_STRING,
                hourlyPrice: labor?.hourlyPrice?.toString() || EMPTY_STRING,
                hourlyCost: labor?.hourlyCost?.toString() || EMPTY_STRING,
              };
            }) || [],
        };

        setInitialData(newData);
        reset(newData);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitForm = (data: IFormService) => {
    if (serviceId) {
      handleEditService(data);
    } else {
      handleAddService(data);
    }
  };

  const handleAddService = async (data: IFormService) => {
    setIsLoading(true);

    const body: IFormService = removeEmptyObjects(data) as IFormService;

    const formData = await createFormData(body, 'serviceImage');

    dispatch(createService(formData))
      .unwrap()
      .then((_res) => {
        handleCancelForm();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditService = async (data: IFormService) => {
    // Filter out completely blank new rows (no id and no meaningful data).
    // These are placeholder rows added by "+ Add Item" that the user never filled in.
    const validMaterials = (data.materials ?? []).filter(
      (m) => m.id || m.materialId || m.quantity || m.unitCost || m.unitPrice
    );
    const validLabors = (data.labors ?? []).filter(
      (l) => l.id || l.hourlyCost || l.hourlyPrice || l.description
    );

    const {
      updated: updatedMaterials,
      created: createdMaterials,
      deleted: deletedMaterials,
    } = convertDataUpdate<IServiceMaterial>(validMaterials, initialData?.materials ?? []);

    const {
      updated: updatedLabors,
      created: createdLabors,
      deleted: deletedLabors,
    } = convertDataUpdate<IServiceLabor>(validLabors, initialData?.labors ?? []);

    // Strip empty-string values from optional numeric fields so the backend
    // Joi schema does not receive "" for Joi.number() fields.
    const sanitizeMaterial = (m: IServiceMaterial): IServiceMaterial => ({
      ...m,
      quantity: m.quantity !== '' ? m.quantity : undefined,
      unitCost: m.unitCost !== '' ? m.unitCost : undefined,
      unitPrice: m.unitPrice !== '' ? m.unitPrice : undefined,
      totalCost: m.totalCost !== '' ? m.totalCost : undefined,
      totalPrice: m.totalPrice !== '' ? m.totalPrice : undefined,
    });
    const sanitizeLabor = (l: IServiceLabor): IServiceLabor => ({
      ...l,
      hourlyCost: l.hourlyCost !== '' ? l.hourlyCost : undefined,
      hourlyPrice: l.hourlyPrice !== '' ? l.hourlyPrice : undefined,
    });

    const dataConvert: IFormUpdateService = {
      ...data,
      materials: {
        deletedMaterials,
        createdMaterials: createdMaterials.map(sanitizeMaterial),
        updatedMaterials: updatedMaterials.map(sanitizeMaterial),
      },
      labors: {
        deletedLabors,
        createdLabors: createdLabors.map(sanitizeLabor) as any,
        updatedLabors: updatedLabors.map(sanitizeLabor) as any,
      },
    };

    const dataChange: IFormService = compareDataUpdate(initialData, dataConvert) as IFormService;

    if (!serviceId || !dataChange) return;

    const formatData: IFormService = {
      ...dataChange,
      ...(dataChange.serviceImage && isEmptyObject(dataChange.serviceImage)
        ? { serviceImage: undefined, deleteAvatar: true }
        : { serviceImage: dataChange.serviceImage }),
    };

    const formData = await createFormData(formatData, 'serviceImage');

    setIsLoading(true);

    dispatch(updateService({ serviceId, body: formData }))
      .unwrap()
      .then((_res) => {
        handleCancelForm();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelForm = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleSearchCategoryChange = (searchKey: string) => {
    setCategorySearchKey(searchKey);
  };

  const handleRemoveMaterial = (index: number) => {
    removeMaterials(index);
  };

  const handleAddItemMaterials = () => {
    appendMaterials(defaultValuesFormMaterial);
  };

  const handleRemoveLabor = (index: number) => {
    removeLabor(index);
  };

  const handleAddItemLabor = () => {
    appendLabor(defaultValuesFormLabor);
  };

  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formServicePage'
      title={t(serviceId ? 'form_service_edit_title' : 'form_service_add_title')}
      onBackToPreviousPage={handleCancelForm}
    >
      <FormProvider
        {...({
          control,
          watch,
          setValue,
          formState: { errors },
        } as UseFormReturn<IFormService>)}
      >
        <Stack
          component={'form'}
          spacing={'20px'}
          onSubmit={handleSubmit(handleSubmitForm)}
          className={cx('form')}
        >
          <Box component={'div'} className={cx('formBody')}>
            <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
              <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                <Grid2 size={12}>
                  <Controller
                    name='serviceImage'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FileUpload
                        accept={FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE}
                        label={t('form_service_label_image')}
                        {...(serviceId && !value?.file && { uploadedFiles: value?.url })}
                        onChange={(files: IFileUpload[]) => onChange(files?.[DEFAULT_NUMBER_ZERO])}
                        errorMessage={errors.serviceImage?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='name'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_service_label_name')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.name?.message}
                        required
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='taskCode'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_service_label_task_code')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.taskCode?.message}
                      />
                    )}
                  />
                </Grid2>
              </Grid2>

              <Grid2 size={12}>
                <Controller
                  name='description'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      multiline
                      height={'auto'}
                      rows={6}
                      label={t('form_service_label_description')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.description?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                <Grid2 size={6}>
                  <Controller
                    name='unit'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseSelect
                        label={t('form_service_label_unit')}
                        value={value}
                        options={mockDataServiceUnit || []}
                        onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                        errorMessage={errors.unit?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='categoryId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseSelect
                        required
                        label={t('form_service_label_category')}
                        value={value}
                        options={listCategory || []}
                        onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                        onSearchChange={handleSearchCategoryChange}
                        isLoading={isLoadingCategory}
                        errorMessage={errors.categoryId?.message}
                      />
                    )}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                <Grid2 size={12}>
                  <Typography color='primary' className={cx('groupTitle')}>
                    {t('field_materials_title')}
                  </Typography>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  {fieldsMaterials?.map((item, index) => (
                    <MaterialService key={item?.id} index={index} onRemoveItem={handleRemoveMaterial} />
                  ))}

                  <Grid2 size={12}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      spacing={'4px'}
                      className={cx('addFieldGroup')}
                    >
                      <img
                        src={icons.commonIconAddField}
                        alt={t('altImg', { ns: 'common/base' })}
                        className={cx('addFieldIcon')}
                      />

                      <Typography color='primary' onClick={handleAddItemMaterials} className={cx('addField')}>
                        {t('common_action_add_field', { ns: 'common/base' })}
                      </Typography>
                    </Stack>
                  </Grid2>
                </Grid2>
              </Grid2>

              <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                <Grid2 size={12}>
                  <Typography color='primary' className={cx('groupTitle')}>
                    {t('field_labor_title')}
                  </Typography>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                  {fieldsLabor?.map((item, index) => (
                    <LaborService key={item?.id} index={index} onRemoveItem={handleRemoveLabor} />
                  ))}

                  <Grid2 size={12}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      spacing={'4px'}
                      className={cx('addFieldGroup')}
                    >
                      <img
                        src={icons.commonIconAddField}
                        alt={t('altImg', { ns: 'common/base' })}
                        className={cx('addFieldIcon')}
                      />

                      <Typography color='primary' onClick={handleAddItemLabor} className={cx('addField')}>
                        {t('common_action_add_field', { ns: 'common/base' })}
                      </Typography>
                    </Stack>
                  </Grid2>
                </Grid2>
              </Grid2>

              <Box flex={1}>
                <Stack direction='column'>
                  <Controller
                    name='isTaxable'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        className={cx('formControlLabel')}
                        slotProps={{
                          typography: {
                            className: cx('checkboxLabel'),
                          },
                        }}
                        control={
                          <Checkbox
                            className={cx('checkbox')}
                            checked={value}
                            onChange={(event) => onChange(event.target.checked)}
                          />
                        }
                        label={t('form_service_label_taxable')}
                      />
                    )}
                  />
                </Stack>

                <Box className={cx('viewLine')} />

                <TotalService />
              </Box>
            </Grid2>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCancelForm}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={serviceId ? !isDirty : false}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>

          {isLoading && <Loading />}

          {/* {isShowPriceBookModal && (
            <PriceBookModal
              businessPriceBook={businessPriceBook}
              handleSubmitForm={handelSubmitFormPriceBook}
              onClose={handleCloseModal}
            />
          )} */}

          <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
        </Stack>
      </FormProvider>
    </ChildrenPage>
  );
};

export default FormService;
